import React, { useState } from "react";
import { View, StyleSheet } from "react-native";
import { WebView } from "react-native-webview";
import { Theme, ThemeConsumer } from "@rneui/themed";
import { ZenText } from "zen-core-elements";
import { ReceiptSettingsFormik } from "./receiptSettings.module";
import CommonService from "../../shared/services/commonService";
import { useAppSelector } from "../../redux/hooks";

interface ReceiptPreviewProps {
    receiptType: "SALES" | "GIFT" | string;
    theme: Theme;
    formikValues: ReceiptSettingsFormik;
}

const ReceiptPreview: React.FC<ReceiptPreviewProps> = ({ receiptType, theme, formikValues }) => {
    const currencySymbol = useAppSelector((state) => state?.common?.currentUser?.value?.purchasedCurrency?.currencySymbol) ?? '₹'
    // Mock Data based on images
    const storeInfo = {
        name: "${storename}",
        address: "${storeaddress}",
        date: "Fri, 24 Nov 2023, 6.45pm",
        orderNo: "#123456",
        soldBy: "${staffname}",
        gstin: "XXXXXXXXXXXXXXXX"
    };

    const giftCardInfo = {
        cardNo: "**** **** **** 1234",
        amount: CommonService.currencyStandard(currencySymbol, "1000.00"),
        expiration: "${expirydate}",
        notes: "${giftcard message}",
        customerName: "${customername}",
        mobileParams: "${mobilenumber}"
    };

    const salesProducts = [
        { name: "Apple", qty: 2, price: CommonService.currencyStandard(currencySymbol, "720"), tax: CommonService.currencyStandard(currencySymbol, "30.45"), total: CommonService.currencyStandard(currencySymbol, "1440.00") },
        { name: "Orange", qty: 1, price: CommonService.currencyStandard(currencySymbol, "200"), tax: CommonService.currencyStandard(currencySymbol, "10.00"), total: CommonService.currencyStandard(currencySymbol, "210.00") },
        { name: "Mango", qty: 1, price: CommonService.currencyStandard(currencySymbol, "250"), tax: CommonService.currencyStandard(currencySymbol, "20.00"), total: CommonService.currencyStandard(currencySymbol, "270.00") },
    ];

    const salesTotals = {
        subtotal: CommonService.currencyStandard(currencySymbol, "1920.00"),
        otherDiscount: "- " + CommonService.currencyStandard(currencySymbol, "0.00"),
        couponDiscount: "- " + CommonService.currencyStandard(currencySymbol, "100.00"), // (ABC123)
        discountedPrice: CommonService.currencyStandard(currencySymbol, "1820.00"),
        addOnPrice: CommonService.currencyStandard(currencySymbol, "20.20"),
        tax: CommonService.currencyStandard(currencySymbol, "30.00"),
        tip: CommonService.currencyStandard(currencySymbol, "10.00"),
        additionalCharge: CommonService.currencyStandard(currencySymbol, "10.00"),
        grandTotal: CommonService.currencyStandard(currencySymbol, "1890.20")
    };

    const paymentModes = [
        { label: "Cash Rounding", value: "- " + CommonService.currencyStandard(currencySymbol, "0.20") },
        { label: "Cash", value: CommonService.currencyStandard(currencySymbol, "1890.00") },
        { label: "Cash Received", value: CommonService.currencyStandard(currencySymbol, "2000.00") }, // Not exactly in list but in image
        { label: "Change due", value: CommonService.currencyStandard(currencySymbol, "110.00") },
    ];

    const DashedDivider = () => (
        <View style={styles.dashedContainer}>
            <View style={[styles.dashedLine, { borderColor: theme.dark }]} />
        </View>
    );

    const renderKeyValue = (key: string, value: string, boldKey = true, boldValue = false) => (
        <View style={styles.row}>
            <ZenText content={key} size="small" bold={boldKey} color={theme.dark} style={{ flex: 1 }} />
            <ZenText content={value} size="small" bold={boldValue} color={theme.dark} style={{ textAlign: 'right', flex: 1 }} />
        </View>
    );

    const renderCenterText = (text: string, bold = false, size: "small" | "medium" | "large" = "small") => (
        <ZenText content={text} size={size} bold={bold} color={theme.dark} style={{ textAlign: "center", marginBottom: 2 }} />
    );

    //Header and footer are saved as HTML from the text editor, older values may still be plain text
    const renderRichText = (text: string, bold = false, size: "small" | "medium" | "large" = "small") => (
        /<[a-z][\s\S]*>/i.test(text)
            ? <HtmlBlock html={text} bold={bold} fontSize={size === "small" ? 13 : size === "medium" ? 15 : 18} color={theme.dark} />
            : renderCenterText(text, bold, size)
    );

    if (receiptType === "GIFT") {
        return (
            <View style={styles.container}>
                {renderCenterText("GIFT RECEIPT", true, "medium")}
                {formikValues?.showStoreInformation ? <>
                    {renderCenterText(storeInfo.name)}
                    {renderCenterText(storeInfo.address)}
                    {renderCenterText(storeInfo.date)}
                </> : null}
                {renderCenterText(`Order No : ${storeInfo.orderNo}`)}
                {formikValues?.showStaffName ? renderCenterText(`Sold by : ${storeInfo.soldBy}`, true) : null}

                <DashedDivider />
                {renderRichText(formikValues?.header ?? "Thank you for Purchasing gift card!", true)}
                <DashedDivider />

                <View style={{ marginVertical: 10, width: '80%', alignSelf: 'center' }}>
                    {renderKeyValue("Card No :", giftCardInfo.cardNo)}
                    {renderKeyValue("Amount :", giftCardInfo.amount)}
                    {renderKeyValue("Expiration :", giftCardInfo.expiration)}
                    {formikValues?.showNotes ? renderKeyValue("Notes :", giftCardInfo.notes) : null}
                </View>

                <DashedDivider />

                <View style={{ marginTop: 10, alignSelf: 'flex-start', paddingLeft: '10%' }}>
                    <ZenText content="Note:" size="small" bold color={theme.dark} />
                    <ZenText content="- This card is redeemable in-store or online." size="small" color={theme.dark} style={{ marginLeft: 10 }} />
                    <ZenText content="- Not refundable or exchangeable." size="small" color={theme.dark} style={{ marginLeft: 10 }} />
                </View>

                {formikValues?.showCustomerInformation ? <View style={{ marginVertical: 20 }}>
                    {renderCenterText(giftCardInfo.customerName)}
                    {renderCenterText(giftCardInfo.mobileParams)}
                </View> : null}
                {/* Footer */}
                <View style={{ marginTop: 20 }}>
                    <DashedDivider />
                    {renderRichText(formikValues?.footer ?? "Thank you for shopping with us!", true, "medium")}
                </View>
            </View>
        );
    }

    // SALES Receipt
    return (
        <View style={styles.container}>
            {renderCenterText("ORDER RECEIPT", true, "medium")}
            {formikValues?.showStoreInformation ? <>
                {renderCenterText(storeInfo.name)}
                {renderCenterText(storeInfo.address)}
                {renderCenterText(`GSTIN : ${storeInfo.gstin}`)}
                {renderCenterText(storeInfo.date)}
            </> : null}
            {renderCenterText(`Order No : ${storeInfo.orderNo}`)}
            {formikValues?.showStaffName ? renderCenterText(`Sold by : ${storeInfo.soldBy}`, true) : null}

            <View style={{ flexDirection: 'row', marginTop: 15, marginBottom: 5, borderBottomWidth: 1, borderBottomColor: theme.dark, paddingBottom: 5 }}>
                <ZenText content="Products" size="small" bold color={theme.dark} style={{ flex: 2 }} />
                <ZenText content="Qty" size="small" bold color={theme.dark} style={{ flex: 1, textAlign: 'center' }} />
                <ZenText content="Price" size="small" bold color={theme.dark} style={{ flex: 1, textAlign: 'right' }} />
                <ZenText content="Tax" size="small" bold color={theme.dark} style={{ flex: 1, textAlign: 'right' }} />
                <ZenText content="Total Price" size="small" bold color={theme.dark} style={{ flex: 2, textAlign: 'right' }} />
            </View>
            <DashedDivider />

            {salesProducts.map((p, i) => (
                <View key={i} style={{ flexDirection: 'row', marginBottom: 5 }}>
                    <ZenText content={p.name} size="small" color={theme.dark} style={{ flex: 2 }} />
                    <ZenText content={p.qty.toString()} size="small" color={theme.dark} style={{ flex: 1, textAlign: 'center' }} />
                    <ZenText content={p.price} size="small" color={theme.dark} style={{ flex: 1, textAlign: 'right' }} />
                    <ZenText content={p.tax} size="small" color={theme.dark} style={{ flex: 1, textAlign: 'right' }} />
                    <ZenText content={p.total} size="small" color={theme.dark} style={{ flex: 2, textAlign: 'right' }} />
                </View>
            ))}

            <DashedDivider />

            <View style={{ width: '100%', marginVertical: 10 }}>
                {renderKeyValue("Subtotal", salesTotals.subtotal, false)}
                {renderKeyValue("Other discount", salesTotals.otherDiscount, false)}
                {renderKeyValue("Coupon discount (ABC123)", salesTotals.couponDiscount, false)}
                {renderKeyValue("Discounted Price", salesTotals.discountedPrice, false)}
                {renderKeyValue("Add on Price", salesTotals.addOnPrice, false)}
                {renderKeyValue("Tax (Sales Tax)", salesTotals.tax, false)}
                {renderKeyValue("Tip", salesTotals.tip, false)}
                {renderKeyValue("Additional Charge", salesTotals.additionalCharge, false)}
            </View>

            <DashedDivider />

            <View style={{ marginVertical: 10 }}>
                {renderKeyValue("Grand Total", salesTotals.grandTotal, true, true)}
            </View>

            {/* Dynamic Payment Part Warning */}
            <View style={{ backgroundColor: 'yellow', padding: 5, marginVertical: 5 }}>
                <ZenText content="THIS PART IS DYNAMIC, IT DEPENDS ON THE MODE OF PAYMENT THE CUSTOMER CHOOSES (any 1 will be applicable)" size="small" color="red" bold style={{ textAlign: 'center' }} />
            </View>

            <View style={{ marginVertical: 10 }}>
                <View style={styles.row}>
                    <ZenText content="Cash Rounding" size="small" color={theme.dark} style={{ flex: 1 }} />
                    <ZenText content={"- " + CommonService.currencyStandard(currencySymbol, "0.20")} size="small" color={theme.dark} style={{ textAlign: 'right', flex: 1 }} />
                </View>
                <View style={styles.row}>
                    <ZenText content="Cash" size="small" color={theme.dark} style={{ flex: 1 }} />
                    <ZenText content={CommonService.currencyStandard(currencySymbol, "1890.00")} size="small" color={theme.dark} style={{ textAlign: 'right', flex: 1 }} />
                </View>
                <View style={styles.row}>
                    <ZenText content="Cash Received" size="small" color={theme.dark} style={{ flex: 1 }} />
                    <ZenText content={CommonService.currencyStandard(currencySymbol, "2000.00")} size="small" color={theme.dark} style={{ textAlign: 'right', flex: 1 }} />
                </View>
                <View style={styles.row}>
                    <ZenText content="Change due" size="small" color={theme.dark} style={{ flex: 1 }} />
                    <ZenText content={CommonService.currencyStandard(currencySymbol, "110.00")} size="small" color={theme.dark} style={{ textAlign: 'right', flex: 1 }} />
                </View>

                {renderCenterText("(Or )", false)}

                {renderKeyValue("Online Payment", CommonService.currencyStandard(currencySymbol, "1890.20"), false)}

                {renderCenterText("(Or )", false)}

                {renderKeyValue("Card", CommonService.currencyStandard(currencySymbol, "1890.20"), false)}

                {renderCenterText("(Or )", false)}

                {renderKeyValue("Gift Card", CommonService.currencyStandard(currencySymbol, "1890.20"), false)}
            </View>

            <DashedDivider />

            {/* Added Footer Part */}
            {formikValues?.showTotalSavings ? <View style={{ marginVertical: 10, alignItems: 'center' }}>
                <ZenText content={"****** You Saved : " + CommonService.currencyStandard(currencySymbol, "352.00") + " today! *******"} size="small" bold color={theme.dark} style={{ marginBottom: 2 }} />
                <ZenText content={"You will earn " + CommonService.currencyStandard(currencySymbol, "160.00") + " loyalty / store credit on this order"} size="small" color={theme.dark} style={{ textAlign: 'center' }} />
            </View> : null}

            <DashedDivider />

            {formikValues?.showCustomerInformation ? <View style={{ marginVertical: 10 }}>
                {renderCenterText("${Customer name}")}
                {renderCenterText("${Mobile number}")}
            </View> : null}

            <View style={{ marginTop: 10 }}>
                {renderRichText(formikValues?.footer ?? "Thank You for shopping with us !", true, "medium")}
            </View>

        </View>
    );
};

//Renders a small HTML snippet centred like the receipt text, growing to fit its content
const HtmlBlock: React.FC<{ html: string, fontSize: number, bold: boolean, color: string }> = ({ html, fontSize, bold, color }) => {
    const [height, setHeight] = useState(fontSize + 8)
    const document = `<!DOCTYPE html><html><head>
        <meta name="viewport" content="width=device-width, initial-scale=1, maximum-scale=1">
        <style>
            html, body { margin: 0; padding: 0; background: transparent; }
            #content { text-align: center; font-family: sans-serif; font-size: ${fontSize}px; font-weight: ${bold ? "bold" : "normal"}; color: ${color}; word-wrap: break-word; }
            #content p, #content div { margin: 0; }
        </style></head>
        <body><div id="content">${html}</div>
        <script>
            function postHeight() { window.ReactNativeWebView.postMessage(String(document.getElementById("content").scrollHeight)); }
            window.onload = postHeight; setTimeout(postHeight, 150);
        </script></body></html>`

    return (
        <WebView
            originWhitelist={["*"]}
            source={{ html: document }}
            style={{ height, width: "100%", backgroundColor: "transparent" }}
            scrollEnabled={false}
            showsVerticalScrollIndicator={false}
            onMessage={(event) => {
                const contentHeight = Number(event?.nativeEvent?.data)
                if (contentHeight) setHeight(contentHeight + 4)
            }}
        />
    )
}

const styles = StyleSheet.create({
    container: {
        padding: 20,
        backgroundColor: 'white',
        width: '100%',
    },
    dashedContainer: {
        height: 1,
        width: '100%',
        marginVertical: 10,
        overflow: 'hidden'
    },
    dashedLine: {
        borderWidth: 1,
        borderStyle: 'dashed',
        borderRadius: 1,
        height: 1
    },
    row: {
        flexDirection: 'row',
        justifyContent: 'space-between',
        marginBottom: 3
    }
});

export default ReceiptPreview;
